import React, { useEffect, useState, useCallback } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import workplaceService from '../../services/workplace.service';
import employeeService from '../../services/employee.service';
import { usePreferences } from '../../contexts/PreferencesContext';
import WelcomeDialog from './WelcomeDialog';
import SetupWizard from './SetupWizard';
import { startAdminTour } from './adminTour';
import {
    getOnboardingState,
    updateOnboardingState,
    START_WIZARD_EVENT,
    START_TOUR_EVENT,
} from './onboardingStorage';

// Orchestrátor onboardingu: welcome modal → setup wizard → (tour).
// Spúšťa sa len pre čerstvé firmy (žiadne pracoviská ani zamestnanci);
// checklist na dashboarde ho vie vyvolať znova cez window eventy.
const OnboardingFlow = ({ currentUser }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { t } = usePreferences();

    const [phase, setPhase] = useState(null); // null | 'welcome' | 'wizard'
    const [workplaces, setWorkplaces] = useState([]);

    const userId = currentUser?.id;

    useEffect(() => {
        if (!userId) return;
        const state = getOnboardingState(userId);
        if (state.welcomeDismissed || state.wizardCompleted || state.wizardSkipped) return;

        let cancelled = false;
        Promise.all([workplaceService.getWorkplaces(), employeeService.getEmployees()])
            .then(([wpRes, empRes]) => {
                if (cancelled) return;
                const wps = wpRes.data || [];
                const emps = empRes.data || [];
                setWorkplaces(wps);
                if (wps.length === 0 && emps.length === 0) {
                    setPhase('welcome');
                }
            })
            .catch(() => {
                // Ticho — onboarding nikdy nesmie zhodiť dashboard
            });

        return () => { cancelled = true; };
    }, [userId]);

    // Externé spustenie z checklistu na dashboarde
    useEffect(() => {
        const onWizard = () => {
            workplaceService.getWorkplaces()
                .then((res) => setWorkplaces(res.data || []))
                .catch(() => setWorkplaces([]));
            setPhase('wizard');
        };
        const onTour = () => startAdminTour(t);
        window.addEventListener(START_WIZARD_EVENT, onWizard);
        window.addEventListener(START_TOUR_EVENT, onTour);
        return () => {
            window.removeEventListener(START_WIZARD_EVENT, onWizard);
            window.removeEventListener(START_TOUR_EVENT, onTour);
        };
    }, [t]);

    const handleWelcomeStart = useCallback(() => {
        updateOnboardingState(userId, { welcomeDismissed: true });
        setPhase('wizard');
    }, [userId]);

    const handleWelcomeSkip = useCallback(() => {
        updateOnboardingState(userId, { welcomeDismissed: true, wizardSkipped: true });
        setPhase(null);
    }, [userId]);

    const handleWizardClose = useCallback(() => {
        updateOnboardingState(userId, { wizardSkipped: true });
        setPhase(null);
    }, [userId]);

    const handleWizardFinish = useCallback((action) => {
        updateOnboardingState(userId, { wizardCompleted: true, wizardSkipped: false });
        setPhase(null);
        if (action === 'tour') {
            setTimeout(() => {
                const started = startAdminTour(t, () => updateOnboardingState(userId, { tourDone: true }));
                if (started === false) updateOnboardingState(userId, { tourDone: true });
            }, 450);
        }
    }, [userId, t]);

    if (!currentUser) return null;

    return (
        <>
            <WelcomeDialog
                open={phase === 'welcome'}
                onStart={handleWelcomeStart}
                onSkip={handleWelcomeSkip}
            />
            <SetupWizard
                open={phase === 'wizard'}
                existingWorkplaces={workplaces}
                onClose={handleWizardClose}
                onFinish={handleWizardFinish}
                canTour={isDesktop}
            />
        </>
    );
};

export default OnboardingFlow;
